/* eslint-disable react/no-array-index-key */
import { isFeatureEnabled, useTranslation } from '@michelin/central-provider';
import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Fab,
  GlobalActions,
  isPossiblePhoneNumber,
  Panel,
  PhoneInput,
  PreventTransitionPrompt,
  Switch,
  TextField,
  useDialogs,
} from '@michelin/acid-components';
import { HoursOfOperation as HoursOfOperationType } from 'Types';
import { useHistory, useParams } from 'react-router-dom';
import { Alert } from '@material-ui/lab';
import { Controller, useForm } from 'react-hook-form';
import { useInitiateDtmfTest, useSaveCommercialERSInformation } from 'hooks/useCommercialERSInformation';
import {
  ArrowBack,
  Edit,
  Save,
  Timelapse,
  DeleteOutline,
  AccessTime,
  Phone,
  CheckCircle,
  ErrorOutline,
  InfoOutlined,
} from '@material-ui/icons';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import HoursOfOperations from 'components/HoursOfOperation/Index';
import { useSnackbar } from 'notistack';
import {
  CheckboxGroup,
  CheckBoxItem,
} from 'components/CommercialServices/CheckboxGroup';
import useUtils from 'hooks/useUtils';
import { GenericActionButtonsMassUpdate } from 'components/GenericActionButtons/GenericActionButtonsMassUpdate';
import { scrollToDisplayFirstError } from 'Utils';
import {
  CommercialERSInformation,
  mechanicalServicesFields,
} from './CommercialERSInformationFields';
import FileUpload from '../FileUpload/FileUpload';

interface ERSDetailsProps {
  action: 'edit' | 'view' | 'add',
  ersInformation: CommercialERSInformation,
  fromMassUpdate?: boolean,
}

function ERSPanel(props: ERSDetailsProps) {
  const { t } = useTranslation();
  const { errorDialog } = useDialogs();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const permissions = usePermissionsSPP();
  const country: string = permissions.location?.customer_country?.toLocaleLowerCase() || 'us';
  const { stNumber, btNumber } = useParams<{ stNumber: string, btNumber: string }>();
  const { getValuesByKey } = useUtils();
  const [uploading, setUploading] = useState<boolean>(false);
  const [mechanicalServicesError, setMechanicalServicesError] = useState<boolean>(false);

  const [tireHooValid, setTireHooValid] = useState<boolean>(true);
  const [mechanicalHooValid, setMechanicalHooValid] = useState<boolean>(true);

  const {
    register,
    formState,
    getValues,
    setValue,
    control,
    watch,
    reset,
    handleSubmit,
  } = useForm<CommercialERSInformation>({ defaultValues: props.ersInformation });

  const { confirmDialog } = useDialogs();

  const saveMutation = useSaveCommercialERSInformation(stNumber);
  const initiateDtmfTestMutation = useInitiateDtmfTest();
  const readOnly: boolean = props.action === 'view';
  const disableUpdateAutodispatch: boolean = !permissions.allowsAction(
    'ers_information.update_is_autodispatch_oncall_cases',
  );
  const phoneTreeSequence = watch('autodispatch_pt_sequence') || [];
  const autodispatchPhoneNumber = watch('autodispatch_phone_number');
  const isPhoneTree = watch('autodispatch_is_phone_tree');
  const waitPeriod = watch('autodispatch_wait_period_for_pt');
  const isPhoneVerified = watch('autodispatch_is_phone_validated');
  const [isPhoneValidated, setIsPhoneValidated] = useState<boolean>(props.ersInformation.autodispatch_is_phone_validated ?? false);
  const [auxWaitPeriod, setAuxWaitPeriod] = useState<string | undefined>(undefined);
  const [auxPhoneTreeSequence, setAuxPhoneTreeSequence] = useState<string[] | undefined>(undefined);
  const { dirtyFields } = formState;
  const [saving, setSaving] = useState<boolean>(false);
  const isDirty = Object.keys(formState.dirtyFields).length > 0;
  const isPhoneTreeEnabled = isFeatureEnabled('autodispatch_oncall_phone_tree');
  const isPhoneVerifiedEnabled = isFeatureEnabled('autodispatch_oncall_phone_verified');
  const isPhoneTreeTestButtonEnabled = isFeatureEnabled('autodispatch_oncall_phone_tree_test_button');
  const [isTestCallActive, setIsTestCallActive] = useState<boolean>(false);

  useEffect(() => {
    if (props.action === 'view') {
      setSaving(false);
    }
  }, [props.action]);
  useEffect(() => {
    const relevantDirty = dirtyFields.autodispatch_phone_number
    || dirtyFields.autodispatch_is_phone_tree
    || dirtyFields.autodispatch_wait_period_for_pt
    || dirtyFields.autodispatch_pt_sequence;
    const currentValidated = getValues('autodispatch_is_phone_validated');
    if (relevantDirty) {
      if (currentValidated) {
        setValue('autodispatch_is_phone_validated', false, { shouldDirty: true });
      }
    } else if (currentValidated !== isPhoneValidated) {
      setValue('autodispatch_is_phone_validated', isPhoneValidated, { shouldDirty: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autodispatchPhoneNumber,
    isPhoneTree,
    waitPeriod,
    phoneTreeSequence,
  ]);

  useEffect(() => {
    const { ersInformation } = props;
    const { autodispatch_phone_number } = ersInformation;
    reset({
      ...ersInformation,
      is_autodispatch_oncall_cases: ersInformation.is_autodispatch_oncall_cases || false,
      autodispatch_after_hours: ersInformation.autodispatch_after_hours || false,
      autodispatch_phone_number: autodispatch_phone_number && !autodispatch_phone_number.includes('+1')
        ? `+1${autodispatch_phone_number}` : autodispatch_phone_number,
      autodispatch_is_phone_tree: ersInformation.autodispatch_is_phone_tree || false,
      autodispatch_pt_sequence: ersInformation.autodispatch_pt_sequence || [],
      autodispatch_wait_period_for_pt: ersInformation.autodispatch_wait_period_for_pt || '0',
      autodispatch_is_phone_validated: ersInformation.autodispatch_is_phone_validated || false,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ersInformation, reset]);

  const onError = (errors: any) => {
    const errorMessages: string[] = getValuesByKey(errors, 'message');
    const message = (errorMessages.filter((m) => m !== '').length > 0 ? errorMessages : t('Please check the marked fields.').split('\n'));

    errorDialog(
      message,
      t('Error saving commercial data.'),
    );
    scrollToDisplayFirstError();
  };

  const save = async (data: CommercialERSInformation) => {
    try {
      if (!formState.isDirty) return;

      const dataToSave = {
        ...data,
        autodispatch_after_hours: data.autodispatch_after_hours || false,
        autodispatch_phone_number: data.autodispatch_phone_number
          ? data.autodispatch_phone_number.replace('+1', '')
          : null,
        autodispatch_is_phone_tree: data.autodispatch_is_phone_tree || false,
        autodispatch_wait_period_for_pt: data.autodispatch_is_phone_tree ? data.autodispatch_wait_period_for_pt : '0',
        autodispatch_pt_sequence: data.autodispatch_is_phone_tree ? data.autodispatch_pt_sequence : [],
        autodispatch_is_phone_validated: data.autodispatch_is_phone_validated || false,
      };

      await saveMutation.mutateAsync({
        stNumber,
        data: dataToSave,
      });
      reset(getValues());
      setIsPhoneValidated(data.autodispatch_is_phone_validated || false);
      setAuxPhoneTreeSequence(undefined);
      setAuxWaitPeriod(undefined);
      enqueueSnackbar(t('Commercial ERS information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
      history.push('view');
    } catch (error) {
      setSaving(false);
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving Commercial ERS Data.'),
      );
    }
  };

  const preSave = async (data: CommercialERSInformation) => {
    setSaving(true);
    if (autodispatchPhoneNumber
      && (formState?.dirtyFields.autodispatch_phone_number
        || formState?.dirtyFields.autodispatch_is_phone_tree
        || formState?.dirtyFields.autodispatch_pt_sequence
        || formState?.dirtyFields.autodispatch_wait_period_for_pt)) {
      const message = isPhoneTreeEnabled
        ? t('The phone number provided is monitored by a person and does not use an automated response.')
        : 'The phone number provided is monitored by a person and does not use an automated response or routing function (phone tree, et al).';
      confirmDialog(
        [message],
        t('Autodispatch Phone Number'),
        t('Yes'),
        t('No'),
        () => save(data),
        () => {
          enqueueSnackbar(t('Your input was not saved. You must enter an approved phone number.'), { variant: 'error' });
          setSaving(false);
        },
      );
    } else if (!autodispatchPhoneNumber && isPhoneTree && isPhoneTreeEnabled) {
      await confirmDialog(
        [
          t(
            // eslint-disable-next-line max-len
            'You have indicated that you have a phone tree but have not provided an Autodispatch Phone Number. Phone tree will not be saved without a valid Autodispatch Phone Number. Do you wish to continue?',
          ),
        ],
        t('Autodispatch Phone Number'),
        t('Yes'),
        t('No'),
        () => {
          const auxData = {
            ...data, autodispatch_is_phone_tree: false, autodispatch_pt_sequence: [], autodispatch_wait_period_for_pt: '0',
          };
          setValue('autodispatch_is_phone_tree', false);
          setValue('autodispatch_pt_sequence', []);
          setValue('autodispatch_wait_period_for_pt', '0');
          save(auxData);
        },
        () => {
          enqueueSnackbar(t('Your input was not saved. You must enter an approved phone number.'), { variant: 'error' });
          setSaving(false);
        },
      );
    } else {
      save(data);
    }
  };

  const addStep = () => {
    setValue('autodispatch_pt_sequence', [...phoneTreeSequence, ''], { shouldDirty: true });
  };

  const handleTestCall = async () => {
    setIsTestCallActive(true);
    setTimeout(() => setIsTestCallActive(false), 5000);
    try {
      const validSteps = (phoneTreeSequence || []).filter((s) => s && s.length > 0);
      await initiateDtmfTestMutation.mutateAsync({
        destinationNumber: autodispatchPhoneNumber || '',
        dtmfConfiguration: validSteps.length > 0 ? validSteps.join(',') : undefined,
        initialWaitPeriodMs: Number(waitPeriod || 0) * 1000,
      });
      enqueueSnackbar(t('Test call initiated successfully.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    } catch (error) {
      const message = error instanceof Error ? error.message : t('Failed to initiate test call. Please try again.');
      enqueueSnackbar(message, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    }
  };

  const copyHoo = () => {
    const newHoo = { ...getValues().tire_services_hours_of_operation };
    setValue('mechanical_hours_of_operation', newHoo || {} as HoursOfOperationType, { shouldDirty: true });
  };

  const ersTireServicesDoYouHaveTrucks = watch('ers_tire_services.do_you_have_trucks');
  const ersTireServicesProvides247365ERSService = watch('ers_tire_services.provides_247365_ers_service');
  const ersMechanicalServicesDoYouHaveTrucks = watch('ers_mechanical_services.do_you_have_trucks');
  const ersMechanicalServicesProvides247365ERSService = watch('ers_mechanical_services.provides_247365_ers_service');
  const onCallAutodispatchEnabled = watch('is_autodispatch_oncall_cases');
  const sameAsTire = watch('ers_mechanical_services.mechanical_same_hours_as_tires_ers_services') || false;

  const ersMechanicalServicesItems: CheckBoxItem<any>[] = [
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.brakes_repair' },
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.diagnostic_services' },
    ...(country === 'us'
      ? [{ key: 'ers_mechanical_services.ers_mechanical_services_provided.dot_inspection' }]
      : [] as any[]),
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.electric_vehicle_charging' },
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.fuel_delivery' },
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.jump_starts' },
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.lights' },
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.lockouts' },
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.suspension_repair' },
    { key: 'ers_mechanical_services.ers_mechanical_services_provided.towing' },
  ];

  const renderActionButtons = () => {
    if (!permissions.allowsAction('location_information.update')) {
      if (!readOnly) history.push('view');
      return null;
    }

    if (props.fromMassUpdate || false) {
      return (
        <GenericActionButtonsMassUpdate
          isDirty={formState.isDirty}
          getValues={getValues}
          reset={reset}
          handleSubmit={handleSubmit}
          validateAction="mass_st_update.update"
          hideAdd
          hideRemove
        />
      );
    }
    return (
      <GlobalActions>
        <Fab
          color="primary"
          aria-label={t('Edit')}
          onClick={() => history.push(`/${btNumber}/commercial/${stNumber}/ers/edit`)}
          disabled={!readOnly || uploading}
        >
          <Edit />
        </Fab>
        <Fab
          color="inherit"
          aria-label={t('Saving')}
          disabled={!saveMutation.isLoading || uploading}
        >
          <Timelapse />
        </Fab>
        <Fab
          color={formState.isDirty ? 'primary' : 'inherit'}
          aria-label={t('Save')}
          onClick={handleSubmit(preSave, onError)}
          disabled={
            saveMutation.isLoading
            || uploading
            || (!saveMutation.isLoading && readOnly)
            || !isDirty
          }
          displayDisabled={!isDirty && !readOnly}
        >
          <Save />
        </Fab>
        <Fab
          color="primary"
          aria-label={t('Back')}
          onClick={() => {
            history.push(`/${btNumber}/commercial/${stNumber}/ers/view`);
          }}
          disabled={saveMutation.isLoading || readOnly || uploading}
        >
          <ArrowBack />
        </Fab>
      </GlobalActions>
    );
  };

  register('ers_mechanical_services.do_you_have_trucks', {
    validate: () => {
      setMechanicalServicesError(false);
      if (getValues().ers_mechanical_services.do_you_have_trucks) {
        const mechanicalServicesDefined: boolean = JSON.stringify(getValues().ers_mechanical_services.ers_mechanical_services_provided || '').includes(':true');
        setMechanicalServicesError(!mechanicalServicesDefined);
        if (!mechanicalServicesDefined) return 'Please select at least one Mechanical Service';
        return true;
      }
      return true;
    },
  });

  return (
    <>
      <Grid container spacing={2}>
        {renderActionButtons()}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Panel title={t('RV/Motor Coach')}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="rv_motor_coach.rv_dealer"
                      control={control}
                      render={(item) => (
                        <Switch
                          label={t('RV Dealer')}
                          readOnly={readOnly}
                          checked={item.field.value || false}
                          onChange={(event) => { item.field.onChange(event.target.checked); }}
                          value={item.field.value || false}
                          color="primary"
                          uncheckedLabel={t('No')}
                          checkedLabel={t('Yes')}
                        />
                      )}
                    />
                    <Controller
                      name="rv_motor_coach.motor_coach_dealer"
                      control={control}
                      render={(item) => (
                        <Switch
                          label={t('Motor Coach Dealer')}
                          readOnly={readOnly}
                          checked={item.field.value || false}
                          onChange={(event) => { item.field.onChange(event.target.checked); }}
                          value={item.field.value || false}
                          color="primary"
                          uncheckedLabel={t('No')}
                          checkedLabel={t('Yes')}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Alert severity="info">
                      {t('Large passenger bus or private motor home. Special lift equipment and jack to install the following sizes: 315/80R22.5, 365/70R22.5, and 295/80R22.5')}
                    </Alert>
                  </Grid>
                </Grid>
              </Panel>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Panel title={t('ERS Tire Services')}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="ers_tire_services.do_you_have_trucks"
                      control={control}
                      render={(item) => (
                        <Switch
                          label={t('Do you have Tire Service Trucks')}
                          readOnly={readOnly}
                          checked={item.field.value || false}
                          onChange={(event) => {
                            item.field.onChange(event.target.checked);
                            if (event.target.checked) {
                              setValue('ers_tire_services.provides_247365_ers_service', false, { shouldDirty: true });
                              setValue('ers_tire_services.provides_247365_ers_service', false, { shouldDirty: true });
                            }
                          }}
                          value={item.field.value || false}
                          color="primary"
                          uncheckedLabel={t('No')}
                          checkedLabel={t('Yes')}
                        />
                      )}
                    />

                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info">{t('Must be able to provide onsite tire service which includes: air compressor and tire changing equipment')}</Alert>
                  </Grid>
                  {ersTireServicesDoYouHaveTrucks && (
                  <>
                    <Grid item xs={6}>
                      <Controller
                        name="ers_tire_services.number_of_trucks"
                        control={control}
                        shouldUnregister
                        rules={{
                          validate: (number_of_trucks: number) => {
                            if (!ersTireServicesDoYouHaveTrucks) return true;
                            if (!number_of_trucks || number_of_trucks <= 0) return false;
                            return true;
                          },
                        }}
                        render={(item) => (
                          <TextField
                            label={t('# of Tire Service Trucks')}
                            type="number"
                            required
                            InputProps={{
                              inputProps: { min: 0 },
                              readOnly,
                            }}
                            onKeyDown={(e: any) => {
                              if (e.key === '-' || e.key === 'v') {
                                e.preventDefault();
                              }
                            }}
                            error={!!item.formState.errors.ers_tire_services?.number_of_trucks}
                            helperText={!!item.formState.errors.ers_tire_services?.number_of_trucks && t('This is a required field that must be greater than 0').toString()}
                            value={item.field.value || ''}
                            readOnly={readOnly}
                            onChange={(event) => { item.field.onChange(event.target.value); }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="ers_tire_services.provides_247365_ers_service"
                        control={control}
                        render={(item) => (
                          <Switch
                            label={t('Provides 24/7/365 Tire ERS Service')}
                            readOnly={readOnly}
                            checked={item.field.value || false}
                            onChange={(event) => { item.field.onChange(event.target.checked); }}
                            value={item.field.value || false}
                            color="primary"
                            uncheckedLabel={t('No')}
                            checkedLabel={t('Yes')}
                          />
                        )}
                      />
                    </Grid>
                  </>
                  )}
                </Grid>
              </Panel>
            </Grid>
            {ersTireServicesDoYouHaveTrucks && (
              <Grid item xs={12}>
                <Panel title={t('Tire ERS Services Hours of Operation')} style={!tireHooValid ? { color: 'red' } : {}}>
                  <Grid container spacing={2}>
                    {!ersTireServicesProvides247365ERSService
                      ? (
                        <Grid item xs={12}>
                          <Controller
                            name="tire_services_hours_of_operation"
                            control={control}
                            shouldUnregister
                            rules={{
                              validate: (newHoo: HoursOfOperationType) => {
                                if (getValues().ers_tire_services.do_you_have_trucks && !getValues().ers_tire_services.provides_247365_ers_service) {
                                  const tireHoursDefined: boolean = JSON.stringify(newHoo || '').includes('"begin":');
                                  setTireHooValid(tireHoursDefined);
                                  return (tireHoursDefined ? true : 'Please specify ERS Tire Services Hours of Operation');
                                }
                                return true;
                              },
                            }}
                            render={(item) => (
                              <HoursOfOperations
                                onChange={(newHoo: HoursOfOperationType) => {
                                  item.field.onChange(newHoo);
                                  if (sameAsTire) copyHoo();
                                }}
                                hoursOfOperation={item.field.value || {} as HoursOfOperationType}
                                readOnly={readOnly}
                                country={country}
                                differentialUpdate={props.fromMassUpdate || false}
                              />
                            )}
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="body2" style={{ fontWeight: 700 }}>
                            {t('Provides 24/7/365 Tire ERS Service')}
                          </Typography>
                        </Grid>
                      )}
                  </Grid>
                </Panel>
              </Grid>
            )}
            <Grid item xs={12}>
              <Panel title={t('ERS Mechanical Services')}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="ers_mechanical_services.do_you_have_trucks"
                      control={control}
                      render={(item) => (
                        <Switch
                          label={t('Do you have Mechanical Service Trucks')}
                          readOnly={readOnly}
                          checked={item.field.value || false}
                          onChange={(event) => {
                            item.field.onChange(event.target.checked);
                            if (event.target.checked) {
                              setValue('ers_mechanical_services.provides_247365_ers_service', false, { shouldDirty: true });
                            } else { // see with fede if we have a better way to improve this and make it dynamic.
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.brakes_repair', false, { shouldDirty: true });
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.diagnostic_services', false, { shouldDirty: true });
                              if (country === 'us') setValue('ers_mechanical_services.ers_mechanical_services_provided.dot_inspection', false, { shouldDirty: true });
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.electric_vehicle_charging', false, { shouldDirty: true });
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.fuel_delivery', false, { shouldDirty: true });
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.jump_starts', false, { shouldDirty: true });
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.lights', false, { shouldDirty: true });
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.lockouts', false, { shouldDirty: true });
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.suspension_repair', false, { shouldDirty: true });
                              setValue('ers_mechanical_services.ers_mechanical_services_provided.towing', false, { shouldDirty: true });
                            }
                          }}
                          value={item.field.value || false}
                          color="primary"
                          uncheckedLabel={t('No')}
                          checkedLabel={t('Yes')}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info">{t('Must be a dedicated Mechanical Service Truck')}</Alert>
                  </Grid>
                  {ersMechanicalServicesDoYouHaveTrucks && (
                    <>
                      {!props.fromMassUpdate && (
                        <Grid item xs={12}>
                          <Controller
                            name="ers_mechanical_services.photo_of_truck"
                            control={control}
                            render={(item) => (
                              <FileUpload
                                error={!!item.formState.errors.ers_mechanical_services?.photo_of_truck || props.fromMassUpdate}
                                data={item.field.value || null}
                                onChange={item.field.onChange}
                                onUploading={setUploading}
                                label={t('View photo of Mechanical Service Truck')}
                                readOnly={readOnly}
                                mode="minimal"
                                downloadEnabled
                              />
                            )}
                          />
                        </Grid>
                      )}
                      <Grid item xs={6}>
                        <Controller
                          name="ers_mechanical_services.number_of_trucks"
                          control={control}
                          shouldUnregister
                          rules={{
                            validate: (number_of_trucks: number) => {
                              if (!ersMechanicalServicesDoYouHaveTrucks) return true;
                              if (!number_of_trucks || number_of_trucks <= 0) return false;
                              return true;
                            },
                          }}
                          render={(item) => (
                            <TextField
                              label={t('# of Mechanical Service Trucks')}
                              type="number"
                              required
                              InputProps={{
                                inputProps: { min: 0 },
                                readOnly,
                              }}
                              onKeyDown={(e: any) => {
                                if (e.key === '-' || e.key === 'v') {
                                  e.preventDefault();
                                }
                              }}
                              error={!!item.formState.errors.ers_mechanical_services?.number_of_trucks}
                              helperText={!!item.formState.errors.ers_mechanical_services?.number_of_trucks && t('This is a required field that must be greater than 0').toString()}
                              value={item.field.value || ''}
                              readOnly={readOnly}
                              onChange={(event) => { item.field.onChange(event.target.value); }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          name="ers_mechanical_services.provides_247365_ers_service"
                          control={control}
                          render={(item) => (
                            <Switch
                              label={t('Provides 24/7/365 Mechanical ERS Service')}
                              readOnly={readOnly}
                              checked={item.field.value || false}
                              onChange={(event) => { item.field.onChange(event.target.checked); }}
                              value={item.field.value || false}
                              color="primary"
                              uncheckedLabel={t('No')}
                              checkedLabel={t('Yes')}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CheckboxGroup
                          title={`${t('Services Provided')} *`}
                          readOnly={readOnly}
                          getValues={getValues}
                          setValue={setValue}
                          watch={watch}
                          control={control}
                          items={ersMechanicalServicesItems}
                          cols={6}
                          labels={mechanicalServicesFields}
                          style={mechanicalServicesError ? { color: 'red' } : {}}
                          shouldUnregister={false}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Panel>
            </Grid>
            {ersMechanicalServicesDoYouHaveTrucks && (
              <Grid item xs={12}>
                <Panel title={t('Mechanical ERS Services Hours of Operation')} style={!mechanicalHooValid ? { color: 'red' } : {}}>
                  <Grid container spacing={2}>
                    {!ersMechanicalServicesProvides247365ERSService
                      ? (
                        <>
                          <Grid item xs={12}>
                            <Controller
                              name="ers_mechanical_services.mechanical_same_hours_as_tires_ers_services"
                              control={control}
                              render={(item) => (
                                <Switch
                                  label={t('Same as Tire ERS Services')}
                                  readOnly={readOnly}
                                  checked={ersTireServicesDoYouHaveTrucks ? item.field.value || false : false}
                                  onChange={(event) => {
                                    item.field.onChange(event.target.checked);
                                    if (event.target.checked) copyHoo();
                                  }}
                                  value={ersTireServicesDoYouHaveTrucks ? item.field.value || false : false}
                                  color="primary"
                                  uncheckedLabel={t('No')}
                                  checkedLabel={t('Yes')}
                                  disabled={!ersTireServicesDoYouHaveTrucks}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            {(ersMechanicalServicesDoYouHaveTrucks && (!ersTireServicesProvides247365ERSService || !sameAsTire))
                                && (
                                  <Controller
                                    name="mechanical_hours_of_operation"
                                    control={control}
                                    shouldUnregister
                                    rules={{
                                      validate: (newHoo: HoursOfOperationType) => {
                                        if (getValues().ers_mechanical_services.do_you_have_trucks && !getValues().ers_mechanical_services.provides_247365_ers_service) {
                                          const mechanicalHoursDefined: boolean = JSON.stringify(newHoo || '').includes('"begin":');
                                          setMechanicalHooValid(mechanicalHoursDefined);
                                          return (mechanicalHoursDefined ? true : 'Please specify ERS Mechanical Services Hours of Operation');
                                        }
                                        return true;
                                      },
                                    }}
                                    render={(item) => (
                                      <HoursOfOperations
                                        googlePlacesId={false}
                                        onChange={(newHoo: HoursOfOperationType) => {
                                          item.field.onChange(newHoo);
                                        }}
                                        hoursOfOperation={item.field.value || {} as HoursOfOperationType}
                                        readOnly={ersMechanicalServicesDoYouHaveTrucks ? readOnly || sameAsTire : false}
                                        country={country}
                                        differentialUpdate={props.fromMassUpdate || false}
                                      />
                                    )}
                                  />
                                )}
                            {(ersMechanicalServicesDoYouHaveTrucks && ersTireServicesProvides247365ERSService)
                                && (
                                  <Typography variant="body2" style={{ fontWeight: 700 }}>
                                    {t('Provides 24/7/365 Mechanical ERS Service')}
                                  </Typography>
                                )}
                          </Grid>
                        </>
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="body2" style={{ fontWeight: 700 }}>
                            {t('Provides 24/7/365 Mechanical ERS Service')}
                          </Typography>
                        </Grid>
                      )}
                  </Grid>
                </Panel>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            {isFeatureEnabled('update_is_fixpix_location')
            && (
            <Grid item xs={12}>
              <Panel title={t('FixPix Mobile App')}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="is_fixpix_location"
                      control={control}
                      render={(item) => (
                        <Switch
                          label={t('Enabled for use and access of the FixPix App')}
                          readOnly={readOnly || !permissions.allowsAction('ers_information.update_is_fixpix_location')}
                          checked={item.field.value || false}
                          onChange={(event) => {
                            item.field.onChange(event.target.checked);
                          }}
                          value={item.field.value || false}
                          color="primary"
                          uncheckedLabel={t('No')}
                          checkedLabel={t('Yes')}
                        />
                      )}
                    />

                  </Grid>
                </Grid>
              </Panel>
            </Grid>
            )}
            {isFeatureEnabled('update_access_to_oncall_dr_dashboard') && (
            <Grid item xs={12}>
              <Panel title={t('ONCall DR Dashboard')}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="oncall_sds_dashboard"
                      control={control}
                      render={(item) => (
                        <Switch
                          label={t('Enabled for use and access of the ONCall DR Dashboard')}
                          readOnly={readOnly || !permissions.allowsAction('ers_information.update_access_to_oncall_dr_dashboard')}
                          checked={item.field.value || false}
                          onChange={(event) => {
                            item.field.onChange(event.target.checked);
                          }}
                          value={item.field.value || false}
                          color="primary"
                          uncheckedLabel={t('No')}
                          checkedLabel={t('Yes')}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Panel>
            </Grid>
            )}
            {!props.fromMassUpdate && isFeatureEnabled('update_autodispatch_oncall_cases')
             && (
             <Grid item xs={12}>
               <Panel title={t('ONCall Autodispatch')}>
                 <Grid container spacing={2}>
                   <Grid item xs={12}>
                     <Controller
                       name="is_autodispatch_oncall_cases"
                       control={control}
                       render={(item) => (
                         <Switch
                           label={t(
                             'Enabled to accept Autodispatch ONCall cases',
                           )}
                           readOnly={readOnly || disableUpdateAutodispatch}
                           checked={item.field.value || false}
                           onChange={(event) => {
                             item.field.onChange(event.target.checked);
                           }}
                           value={item.field.value || false}
                           color="primary"
                           uncheckedLabel={t('No')}
                           checkedLabel={t('Yes')}
                         />
                       )}
                     />
                   </Grid>
                   <Grid item xs={12}>
                     <div style={{
                       display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                     }}
                     >
                       <div
                         style={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: '6px',
                           flex: 1,
                         }}
                       >
                         <Typography
                           variant="body2"
                           style={{
                             color: (readOnly || disableUpdateAutodispatch) ? 'rgba(0, 0, 0, 0.3)' : undefined,
                           }}
                         >
                           {t('Enable ONCall Auto Dispatch After Regular Service Hours')}
                         </Typography>
                         <Tooltip
                           title={t("If enabled, Michelin ONCall's AutoDispatch Agent will call the technicians specified in the location's Tech Rotation page to dispatch an ONCall case.").toString()}
                           placement="top-end"
                         >
                           <InfoOutlined fontSize="small" style={{ display: 'block', color: 'rgb(102, 102, 102)' }} />
                         </Tooltip>
                       </div>
                       <Controller
                         name="autodispatch_after_hours"
                         control={control}
                         render={(item) => (
                           <Switch
                             hideLabel
                             label=""
                             fullWidth={false}
                             readOnly={readOnly || disableUpdateAutodispatch}
                             checked={item.field.value || false}
                             onChange={(event) => {
                               item.field.onChange(event.target.checked);
                             }}
                             value={item.field.value || false}
                             color="primary"
                             uncheckedLabel={t('No')}
                             checkedLabel={t('Yes')}
                           />
                         )}
                       />
                     </div>
                   </Grid>
                   <Grid item xs={12}>
                     <Grid container spacing={2} justify="center">
                       <Grid item xs={12} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                         <Phone
                           fontSize="small"
                           style={{
                             color: (readOnly || disableUpdateAutodispatch)
                               ? 'rgb(179, 179, 179)'
                               : 'rgb(26, 26, 26)',
                           }}
                         />
                         <Typography
                           variant="body2"
                           style={{
                             fontWeight: 400,
                             color: (readOnly || disableUpdateAutodispatch)
                               ? 'rgb(179, 179, 179)'
                               : 'rgb(26, 26, 26)',
                           }}
                         >
                           {t('Autodispatch Phone Number')}
                         </Typography>
                       </Grid>
                       <Grid item xs={isPhoneVerifiedEnabled ? 11 : 12}>
                         <Controller
                           name="autodispatch_phone_number"
                           control={control}
                          // If the flag is false and there is not phone number the validation pass. But if the phone number is set, it should be valid.
                           rules={{
                             validate: (value: string) => {
                               if ((!onCallAutodispatchEnabled && !value)
                                 || isPossiblePhoneNumber(value)) {
                                 return true;
                               }
                               return t('You must enter a valid Autodispatch phone number').toString();
                             },
                           }}
                           render={(item) => (
                             <>
                               <PhoneInput
                                 id="autodispatch_phone_number"
                                 value={item.field.value || ''}
                                 defaultCountry={country === 'ca' ? 'CA' : 'US'}
                                 hideCountryIcon
                                 readOnly={readOnly || disableUpdateAutodispatch}
                                 required={onCallAutodispatchEnabled}
                                 onChange={(e) => {
                                   item.field.onChange(e.value || '');
                                 }}
                               />
                             </>
                           )}
                         />
                       </Grid>
                       { isPhoneVerifiedEnabled && (
                       <Grid item xs={1}>
                         {isPhoneVerified ? (
                           <Tooltip title={t('Phone number was validated').toString()}>
                             <CheckCircle style={{ color: 'green' }} />
                           </Tooltip>
                         ) : (
                           <Tooltip title={t('Phone number was not validated').toString()}>
                             <ErrorOutline style={{ color: 'orange' }} />
                           </Tooltip>
                         )}
                       </Grid>
                       )}
                     </Grid>
                   </Grid>
                   { isPhoneTreeEnabled && (
                   <Grid item xs={12}>
                     <Controller
                       name="autodispatch_is_phone_tree"
                       control={control}
                       render={(item) => (
                         <Switch
                           label={t('Do you have a phone tree?')}
                           readOnly={readOnly || disableUpdateAutodispatch}
                           checked={item.field.value || false}
                           onChange={(event) => {
                             item.field.onChange(event.target.checked);
                             if (event.target.checked) {
                               if (auxPhoneTreeSequence) {
                                 setValue('autodispatch_pt_sequence', auxPhoneTreeSequence, {
                                   shouldDirty: true,
                                 });
                               } else if (!phoneTreeSequence || phoneTreeSequence.length === 0) {
                                 setValue('autodispatch_pt_sequence', [''], {
                                   shouldDirty: true,
                                 });
                               }
                               if (auxWaitPeriod) {
                                 setValue('autodispatch_wait_period_for_pt', auxWaitPeriod, {
                                   shouldDirty: true,
                                 });
                               }
                             } else {
                               setAuxPhoneTreeSequence(phoneTreeSequence);
                               setAuxWaitPeriod(waitPeriod);
                               setValue('autodispatch_pt_sequence', undefined, {
                                 shouldDirty: true,
                               });
                               setValue('autodispatch_wait_period_for_pt', undefined, {
                                 shouldDirty: true,
                               });
                             }
                           }}
                           value={item.field.value || false}
                           color="primary"
                           uncheckedLabel={t('No')}
                           checkedLabel={t('Yes')}
                         />
                       )}
                     />
                   </Grid>
                   )}
                   { isPhoneTreeEnabled && (
                   <Grid item xs={12}>
                     {isPhoneTree && (
                     <Grid container spacing={2} justify="center">
                       <Grid item xs={8} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                         <AccessTime
                           fontSize="small"
                           style={{
                             color: (readOnly || disableUpdateAutodispatch)
                               ? 'rgb(179, 179, 179)'
                               : 'rgb(26, 26, 26)',
                           }}
                         />
                         <Typography
                           variant="body2"
                           style={{
                             fontWeight: 400,
                             color: (readOnly || disableUpdateAutodispatch)
                               ? 'rgb(179, 179, 179)'
                               : 'rgb(26, 26, 26)',
                           }}
                         >
                           {t('Wait Period')}
                         </Typography>
                       </Grid>
                       <Grid item xs={8}>
                         <Controller
                           name="autodispatch_wait_period_for_pt"
                           control={control}
                           render={(item) => (
                             <TextField
                               id="wait_period_for_phone_tree"
                               variant="outlined"
                               type="number"
                               style={{ display: 'flex', flexWrap: 'wrap' }}
                               onChange={(e) => {
                                 item.field.onChange(e.target.value);
                               }}
                               value={item.field.value || '0'}
                               readOnly={readOnly}
                               InputProps={{
                                 inputProps: { min: 0 },
                                 readOnly: readOnly || disableUpdateAutodispatch,
                                 endAdornment: (
                                   <InputAdornment position="end">
                                     {t('seconds')}
                                   </InputAdornment>
                                 ),
                               }}
                             />
                           )}
                         />
                       </Grid>
                       <Grid item xs={8} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                         <Typography
                           style={{
                             fontWeight: 600,
                             fontSize: '20px',
                             color: (readOnly || disableUpdateAutodispatch)
                               ? 'rgb(179, 179, 179)'
                               : 'rgb(26, 26, 26)',
                           }}
                         >
                           #
                         </Typography>
                         <Typography
                           variant="body2"
                           style={{
                             fontWeight: 400,
                             color: (readOnly || disableUpdateAutodispatch)
                               ? 'rgb(179, 179, 179)'
                               : 'rgb(26, 26, 26)',
                           }}
                         >
                           {t('Navigation Sequence')}
                         </Typography>
                       </Grid>
                       <Controller
                         name="autodispatch_pt_sequence"
                         control={control}
                         rules={{
                           validate: (step1: string[]) => {
                             if (!step1) return true;
                             if (!autodispatchPhoneNumber && !getValues('is_autodispatch_oncall_cases')) return true;
                             const invalidIndexes: number[] = [];
                             for (let i = 0; i < step1.length; i++) {
                               const step = step1[i];
                               if (!step || !/^[0-9*#]+$/.test(step)) {
                                 invalidIndexes.push(i + 1);
                               }
                             }
                             const amountOfInvalid = invalidIndexes.length;
                             if (amountOfInvalid === 0) {
                               return true;
                             } if (amountOfInvalid === 1) {
                               return t('The navigation sequence can only have digits, * or # and not be empty. Step {{step}} is invalid.', { step: invalidIndexes[0] }).toString();
                             }
                             return t('The navigation sequence can only have digits, * or # and not be empty. Steps {{steps}} are invalid.',
                               { steps: invalidIndexes.join(', ') }).toString();
                           },
                         }}
                         render={({ field, fieldState }) => {
                           const steps = field.value ?? [];
                           const errorMsg = fieldState.error?.message ?? '';
                           return (

                             <>
                               {field.value && field.value.length > 0 && field.value.map((step, index) => (
                                 <Grid item xs={8} key={`phone_tree_step_${index}_item`}>
                                   <Grid
                                     container
                                     alignItems="center"
                                     justify="space-evenly"
                                     key={`phone_tree_step_${index}_grid`}
                                   >
                                     <Grid item xs={2}>
                                       <Typography
                                         variant="body1"
                                         color="textSecondary"
                                       >
                                         #
                                         {index + 1}
                                       </Typography>
                                     </Grid>
                                     <Grid item xs={8}>
                                       <TextField
                                         id={`phone_tree_step_${index}_input`}
                                         variant="outlined"
                                         placeholder={t('Enter digits, #, or *')}
                                         value={step}
                                         onChange={(e) => {
                                           const rawValue = e.target.value;
                                           const filtered = rawValue.replace(/[^0-9*#]/g, '');
                                           const newSteps = [...steps];
                                           newSteps[index] = filtered;
                                           field.onChange(newSteps);
                                         }}
                                         error={errorMsg.includes(` ${index + 1}`)}
                                         readOnly={readOnly || disableUpdateAutodispatch}
                                         maxLength={10}
                                         data-testid="phone-tree-step-input"
                                       />
                                     </Grid>
                                     <Grid item xs={2} style={{ textAlign: 'center' }}>
                                       <IconButton
                                         onClick={() => {
                                           const newSteps = [...steps];
                                           newSteps.splice(index, 1);
                                           field.onChange(newSteps);
                                         }}
                                         disabled={steps.length <= 1 || readOnly || disableUpdateAutodispatch}
                                         color="primary"
                                       >
                                         <DeleteOutline />
                                       </IconButton>
                                       {/* )} */}
                                     </Grid>
                                   </Grid>
                                 </Grid>
                               ))}
                             </>
                           );
                         }}
                       />
                       <Grid item xs={12} />
                       {!disableUpdateAutodispatch && !readOnly && (
                       <>
                         <Grid item xs={8}>
                           <Grid container spacing={2} justify="center">
                             <Button
                               color="primary"
                               variant="outlined"
                               onClick={addStep}
                               disabled={
                                    (getValues().autodispatch_pt_sequence?.length
                                      || 0) >= 7
                                  }
                             >
                               {t('+ Add Step')}
                             </Button>
                           </Grid>
                         </Grid>
                       </>
                       )}
                     </Grid>
                     )}
                   </Grid>
                   )}
                   {isPhoneTreeEnabled && isPhoneTreeTestButtonEnabled && isPhoneTree && !disableUpdateAutodispatch && (
                   <Grid item xs={12} style={{ marginTop: '8px' }}>
                     <Button
                       fullWidth
                       color="primary"
                       variant="contained"
                       onClick={handleTestCall}
                       disabled={isTestCallActive || !autodispatchPhoneNumber}
                       startIcon={isTestCallActive ? <CircularProgress size={16} color="inherit" /> : <Phone />}
                     >
                       {isTestCallActive ? t('Calling...') : t('Test Call')}
                     </Button>
                   </Grid>
                   )}
                   {disableUpdateAutodispatch && (
                   <Grid item xs={12}>
                     <Alert severity="info">
                       {t(
                         'ONCall Autodispatch options can only be edited by an Account Manager',
                       )}
                     </Alert>
                   </Grid>
                   )}
                 </Grid>
               </Panel>
             </Grid>
             )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <br />
          <br />
        </Grid>
      </Grid>
      <PreventTransitionPrompt
        when={isDirty && !saving}
        handleDiscard={() => reset()}
      />
    </>
  );
}

export default ERSPanel;
